import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";

const SUBMIT_URL = "https://readdy.ai/api/form/da29gdbk8r5te5mqsk8g";

const contactInfo = [
  { icon: "ri-mail-line", label: "联系邮箱", value: "hello@zhengbanhanmandaquan.example" },
  { icon: "ri-customer-service-2-line", label: "反馈与建议", value: "任何建议我们都会认真阅读" },
  { icon: "ri-time-line", label: "回复时间", value: "通常在 1-3 个工作日内回复" },
];

export default function ContactPage() {
  usePageMeta("联系我们 - 正版韩漫大全", "联系我们获取正版韩漫大全的帮助、反馈与建议，我们会在 1-3 个工作日内回复。");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // anti-spam honeypot: silently ignore bot submissions
    const hp = String(data.get("website_alt") ?? "").trim();
    if (hp) {
      setStatus("success");
      form.reset();
      return;
    }
    data.delete("website_alt");

    setStatus("loading");
    setFormError("");
    try {
      const payload = new URLSearchParams();
      data.forEach((value, key) => {
        if (key === "content") {
          const files = form.querySelector<HTMLInputElement>('input[name="content"]');
          if (files && files.files && files.files.length > 0) {
            payload.append(key, "Uncollectable");
            return;
          }
        }
        payload.append(key, String(value));
      });
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "";
      if (!res.ok || !parsed || parsed.code !== "OK" || /spam/i.test(serverMsg)) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }
      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setFormError(err instanceof Error ? err.message : "网络异常，请稍后重试。");
    }
  }

  return (
    <div className="bg-background-50">
      <div className="bg-primary-50/50">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav className="text-xs text-foreground-500" aria-label="面包屑">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-800" aria-current="page">联系我们</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-900 md:text-3xl">联系我们</h1>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            有任何关于正版韩漫大全的问题、建议或合作意向，欢迎通过以下方式与我们联系。
          </p>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 py-12 md:px-6 md:py-16 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          {contactInfo.map((item) => (
            <div key={item.label} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className={`text-xl ${item.icon}`}></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-foreground-900">{item.label}</p>
                <p className="mt-1 text-sm text-foreground-600">{item.value}</p>
              </div>
            </div>
          ))}
          <div className="rounded-lg bg-accent-100 p-5">
            <p className="text-sm font-semibold text-accent-900">正版阅读倡导</p>
            <p className="mt-2 text-sm leading-relaxed text-accent-900/90">
              我们始终倡导在官方正版渠道阅读韩漫，尊重每一位创作者与版权方。
            </p>
          </div>
        </div>

        <div className="lg:col-span-2">
          <form
            onSubmit={handleSubmit}
            data-readdy-form="正版韩漫大全联系表单"
            className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
          >
            <h2 className="font-heading text-lg font-bold text-foreground-900">给我们留言</h2>
            <p className="mt-1 text-sm text-foreground-600">请填写以下信息，我们会尽快与你联系。</p>

            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">你的称呼</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  maxLength={50}
                  placeholder="请输入你的称呼"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">联系邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  maxLength={120}
                  placeholder="用于接收我们的回复"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-800">咨询主题</label>
              <select
                id="subject"
                name="subject"
                required
                className="w-full rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">请选择咨询主题</option>
                <option value="官方入口">官方入口咨询</option>
                <option value="正版渠道">正版渠道建议</option>
                <option value="内容反馈">内容反馈</option>
                <option value="合作意向">合作意向</option>
                <option value="其他">其他</option>
              </select>
            </div>

            <div className="mt-4">
              <label htmlFor="content" className="mb-1.5 block text-sm font-medium text-foreground-800">留言内容</label>
              <textarea
                id="content"
                name="content"
                required
                rows={5}
                maxLength={500}
                placeholder="请描述你的问题或建议（不超过 500 字）"
                className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
            </div>

            {/* anti-spam honeypot */}
            <div className="form-hp">
              <label htmlFor="website_alt">Website</label>
              <input id="website_alt" name="website_alt" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>

            {status === "success" && (
              <div className="mt-5 flex items-center gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-lg"></i> 提交成功！我们会尽快与你联系。
              </div>
            )}
            {status === "error" && (
              <div className="mt-5 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-900">
                <i className="ri-error-warning-line text-lg"></i>
                <span>{formError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {status === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i> 提交中…
                </>
              ) : (
                <>提交留言 <i className="ri-send-plane-line"></i></>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}